import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button, { RemoveButton } from '@/components/Button';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useQueueSettings from '@/hooks/dq/useSettings';
import useAlerts from '@/hooks/useAlerts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { scrollBarStyles } from '@/utils/theme';

import { postTypes } from './NewPostModal';

const Dialog = styled(StyledDialog)`
  width: 460px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7e7e7;
  box-shadow: 0px 1px 0px 0px #22222214;
`;

const Body = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* height: 400px;
  overflow-y: auto;
  ${scrollBarStyles}; */
`;

const Footer = styled.div`
  border-radius: 0 0 4px 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  height: 112px;
  padding: 0 24px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const Row = styled.div`
  gap: 12px;
  display: flex;
  align-items: center;
  > div {
    flex: 1;
  }
`;

const StyledRemoveButton = styled(RemoveButton)`
  height: 46px;
  width: 46px;
  border-color: ${({ theme }) => theme.colors.grayscale};
  background: ${({ theme }) => theme.colors.white};
`;

const Switch = styled.div`
  background: ${({ theme }) => theme.colors.white};
`;

const options = [
  ...postTypes.map(({ label, type }) => ({
    label,
    value: type,
  })),
  {
    label: 'Meme Image',
    value: 'meme',
  },
];

type FormDataT = {
  resorter: Array<{
    post_type: EditorialFacebookPostType | string;
  }>;
  enabled: boolean;
};

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const ResorterSettingsModal: FC<Props> = ({ onClose, activePage }) => {
  const queryClient = useQueryClient();
  const settings = useQueueSettings(activePage);
  const [network] = useNetwork();
  const alerts = useAlerts();

  const {
    control,
    reset,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      resorter:
        settings?.resorterSettings?.post_types.map((t) => ({ post_type: t })) ??
        [],
      enabled: settings?.resorterSettings?.enabled ?? false,
    },
  });

  const { fields, append, remove } = useFieldArray<FormDataT>({
    control,
    name: 'resorter',
  });

  const onSubmit = () => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post('/v1/editorial/queue/resorter', {
          channel_id: activePage.id,
          enabled: data.enabled,
          post_types: data.resorter.map((t) => t.post_type),
          network,
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_queue_channels',
        ]);

        await queryClient.invalidateQueries([
          'editorial_distribution_schedules',
          {
            network,
            queueable: true,
            status: 'queued',
            selectedChannels: activePage.id.toString(),
          },
        ]);

        alerts.success('Settings saved');

        reset(data);
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })();
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">Resorter Settings</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Body>
          <Switch>
            <FormField<FormDataT>
              control={control}
              name={'enabled'}
              type="switch"
              headline="Automated posts resorting enabled"
            />
          </Switch>
          {fields.map((field, i) => (
            <Row key={field.id}>
              <FormField<FormDataT, EditorialFacebookPostType | string>
                control={control}
                name={`resorter.${i}.post_type`}
                type="select"
                options={options}
              />
              <StyledRemoveButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(i);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </StyledRemoveButton>
            </Row>
          ))}
          <Button
            type="button"
            $type="colorized"
            onClick={() => append({ post_type: postTypes[0].type })}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 2V14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 8H14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Add post type
          </Button>
        </Body>
        <Footer>
          <Button
            type="button"
            color="primary"
            disabled={isSubmitting || !isDirty || !fields.length}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Save
          </Button>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default ResorterSettingsModal;
